import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Cookies from 'js-cookie'
import { Alert } from '@fs/zion-ui'
import { i18n, listOfMonths, listOfDaysInWeek } from '@fs/zion-locale'
import { format, formatDistanceStrict, addHours, addMinutes } from '@fs/zion-locale/date-fns'
import zionDebug from '@fs/zion-debug'

const debug = zionDebug('zion:root:outage-banner')

function formatTime(hours, minutes, military) {
  if (military) {
    hours = hours < 10 ? `0${hours}` : hours
  } else {
    hours %= 12
    hours = hours || 12 // the hour '0' should be '12'
  }
  minutes = minutes < 10 ? `0${minutes}` : minutes
  return `${hours}:${minutes}`
}

function generateVars(date, numOfHoursOrMins = 1, showHours = true) {
  const utcOffset = date.getTimezoneOffset()
  const utcOffsetHours = Math.floor(utcOffset / 60)
  const utcOffsetMinutes = utcOffset % 60
  const durationNum = parseInt(numOfHoursOrMins, 10)
  const duration = showHours
    ? formatDistanceStrict(addHours(new Date(), durationNum))
    : formatDistanceStrict(addMinutes(new Date(), durationNum))

  return {
    weekday: listOfDaysInWeek()[date.getDay()],
    month: listOfMonths()[date.getMonth()],
    ordinalDayOfMonth: format(date, 'do'),
    time: format(date, 'p'),
    militaryTime: formatTime(date.getHours(), date.getMinutes(), true),
    utcPlusMinus: utcOffset > -1 ? '-' : '+',
    utcOffset: formatTime(utcOffsetHours, utcOffsetMinutes, true),
    duration,
  }
}
function getTimeDateInSeconds(dateString) {
  return Math.trunc(new Date(dateString).getTime() / 1000)
}
function calculateDuration(outageBegin, outageEnd) {
  const outageTimeInSeconds = getTimeDateInSeconds(new Date(outageEnd).valueOf() - new Date(outageBegin).valueOf())
  const outageTimeInMinutes = outageTimeInSeconds / 60
  const showHours = outageTimeInMinutes % 60 === 0
  const hourOrMinuteDisplayNum = `${showHours ? outageTimeInMinutes / 60 : outageTimeInMinutes}`
  return {
    hourOrMinuteDisplayNum,
    showHours,
  }
}
function setBannerKey(planned, fullOutage) {
  const preKey = 'global'
  let bannerKey
  if (!planned) {
    bannerKey = `${preKey}Alert`
  } else if (fullOutage) {
    bannerKey = `${preKey}UnavailableAlert`
  } else {
    bannerKey = `${preKey}PartialAlert`
  }
  debug(`bannerKey: ${bannerKey}`)
  return bannerKey
}
function handleClose(id, setClosed) {
  setClosed(true)
  const dismissCookie = `${id}`
  Cookies.set(dismissCookie, 'dismissed', '/')
}

export default function OutageBanner({ id, planned, fullOutage, displayBegin, outageBegin, outageEnd, lastUpdated }) {
  debug(`
    id: ${id},
    planned: ${planned},
    fullOutage: ${fullOutage},
    displayBegin: ${displayBegin},
    outageBegin: ${outageBegin},
    outageEnd: ${outageEnd},
    lastUpdated: ${lastUpdated},`)

  const [closed, setClosed] = useState(false)

  const outageBeginDate = new Date(outageBegin)
  const outageEndDate = new Date(outageEnd)
  const { hourOrMinuteDisplayNum, showHours } = calculateDuration(outageBeginDate, outageEndDate)
  const vars = generateVars(outageBeginDate, hourOrMinuteDisplayNum, showHours)
  const bannerKey = setBannerKey(planned, fullOutage)
  const text = i18n.t(bannerKey, `alert text ${bannerKey} {duration}`, vars)
  return (
    !closed && (
      <Alert type="warning" onDismiss={() => handleClose(id, setClosed)} outlined dismissable dense>
        {text}
      </Alert>
    )
  )
}

OutageBanner.propTypes = {
  id: PropTypes.number.isRequired,
  planned: PropTypes.bool.isRequired,
  fullOutage: PropTypes.bool.isRequired,
  displayBegin: PropTypes.string,
  outageBegin: PropTypes.string.isRequired,
  outageEnd: PropTypes.string.isRequired,
  lastUpdated: PropTypes.string,
}
